import _ from 'lodash';
import * as util from '#packages/util';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { runInContext } from '../../../common/utils';
import { EditorType, InstallInitiator } from '@wix/platform-editor-sdk';
import { widgetPlugins } from './widgetPlugins';
import experiment from 'experiment';
import type { EditorAPI } from '#packages/editorAPI';
import type { AppData } from '@wix/document-services-types';

interface Options {
  appDefinitionId: string;
  widget: Record<string, unknown>;
  originInfo: { biData: Record<string, AnyFixMe> };
  page: {
    isHidden: boolean;
  };
  [key: string]: AnyFixMe;
}

function isApplicationInstalled(
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: unknown,
  options: Options,
) {
  return new Promise(function (resolve, reject) {
    if (!options?.appDefinitionId) {
      reject(new Error('options must contain appDefinitionId'));
      return;
    }

    const isAppInstalled = editorAPI.dsRead.tpa.app.isInstalled(
      options.appDefinitionId,
    );
    resolve(isAppInstalled);
  });
}

function addApplication(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: unknown,
  options: Options,
) {
  return new Promise(function (resolve, reject) {
    if (!options?.appDefinitionId) {
      reject(new Error('options must contain appDefinitionId'));
      return;
    }

    util.fedopsLogger.interactionStarted(
      util.fedopsLogger.INTERACTIONS.INSTALL_APP,
    );

    if (
      options.appDefinitionId ===
      //@ts-expect-error
      editorAPI.platform.editorApps.WIX_CODE.appDefId
    ) {
      editorAPI.wixCode.provision({
        onSuccess: resolve,
        onError: reject,
      });

      return;
    }

    options = {
      ...options,
      platformOrigin: {
        type: EditorType.Classic,
        initiator: InstallInitiator.App,
        info: {
          appDefinitionId: appData?.appDefinitionId,
          ...options.originInfo,
        },
      },
      biData: {
        ...options.biData,
        ...options.originInfo?.biData,
      },
    };

    runInContext(appData.appDefinitionId, editorAPI, () =>
      editorAPI.tpa
        .addApplication(options.appDefinitionId, options)
        .then(function (data) {
          util.fedopsLogger.interactionEnded(
            util.fedopsLogger.INTERACTIONS.INSTALL_APP,
          );

          resolve(data);
        })
        .catch(function (error) {
          ErrorReporter.captureException(error, {
            tags: { appInstallFailed: options.appDefinitionId },
            extra: { appData, options },
          });

          reject(error);
        }),
    );
  });
}

function addComponent(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: unknown,
  options: Options,
) {
  return new Promise(function (resolve, reject) {
    // @ts-expect-error
    options = options || {};
    if (!options.componentType) {
      reject(new Error('options must contain componentType'));
      return;
    }

    if (!options.appDefinitionId) {
      options.appDefinitionId = appData.appDefinitionId;
    }

    if (options.componentType === 'WIDGET') {
      if (!_.isObject(options.widget)) {
        reject(new Error('options must contain a widget object'));
        return;
      }
      options.widget.tpaWidgetId = options.widget.widgetId;
    } else if (options.componentType === 'PAGE') {
      if (!_.isObject(options.page)) {
        reject(new Error('options must contain a page object'));
        return;
      }
      if (options.page.isHidden && !_.isBoolean(options.page.isHidden)) {
        reject(new Error('isHidden param should be of type boolean'));
        return;
      }
    }

    runInContext(appData.appDefinitionId, editorAPI, () =>
      editorAPI.tpa
        .addComponent(options)
        .then(function (data) {
          resolve(data);
        })
        .catch(function (error) {
          reject(error);
        }),
    );
  });
}

function isAppSectionInstalled(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: unknown,
  options: Options,
) {
  return new Promise(function (resolve, reject) {
    if (!options?.sectionId) {
      reject(new Error('options must contain sectionId'));
      return;
    }

    let isSectionInstalled;

    if (experiment.isOpen('se_appDefIdUpdates')) {
      isSectionInstalled =
        editorAPI.dsRead.tpaV2.section.isSectionInstalledByTpaPageId(
          appData.appDefinitionId,
          options.sectionId,
        );
    } else {
      let { applicationId } = appData;
      if (options.appDefinitionId) {
        const otherAppData = editorAPI.dsRead.tpa.app.getDataByAppDefId(
          options.appDefinitionId,
        );
        applicationId = otherAppData?.applicationId;
      }

      isSectionInstalled =
        editorAPI.dsRead.tpa.section.isSectionInstalledByTpaPageId(
          applicationId,
          options.sectionId,
        );
    }

    resolve(isSectionInstalled);
  });
}
const add = {
  application: addApplication,
  component: addComponent,
};

export { add, isApplicationInstalled, isAppSectionInstalled, widgetPlugins };
